import { Controller } from "@hotwired/stimulus"

// Keys that move a focused range; only these hand it back its template step
// (Tab or Shift on the slider must not snap a typed value).
const RANGE_KEYS = new Set([
  "ArrowLeft",
  "ArrowRight",
  "ArrowUp",
  "ArrowDown",
  "PageUp",
  "PageDown",
  "Home",
  "End",
])

/**
 * Inspections measurement slider (CONEK-4653, spec §6.3 #5).
 *
 * Owns ONLY the slider's local presentation: the live value bubble, the
 * disabled-reason hint text and (CONEK-8195) the exact-value number box.
 * Threshold/enabled-if SCORING and the diagram swap live in
 * `inspections--severity-engine` (which reads the same data attributes and
 * the range's value); this controller keeps the bubble and the box in sync
 * and exposes the disabled hint so the engine can surface it when an
 * enabled-if gate is unsatisfied.
 *
 * Number box (CONEK-8195): typing a value moves the range to it and
 * dispatches `input` on the range, so the bubble updates and the severity
 * engine recolours the slider and the page pill. The range is the field that
 * is saved, so it must hold the typed value EXACTLY: a native range snaps to
 * its `step`, so its step is relaxed to `any` while it holds a typed value
 * and put back from `data-step` as soon as the inspector grabs the slider
 * (pointer, or a key that moves it). Dragging writes the value back into the
 * box. Markup without a number box (an older conek_ui) keeps working.
 *
 * A reading saved off-step (3.5 on a 1 mm slider, typed in the box) comes back
 * as the range's `value` attribute, but the browser snaps the range's value to
 * the template step as it parses the page: a plain Next then saved 4 in its
 * place. So `connect()` puts the stored reading back and relaxes the step, as
 * typing it did, until the inspector grabs the slider.
 *
 * Markup (rendered by Ui::MeasurementSliderComponent):
 *   <div data-controller="inspections--measurement-slider"
 *        data-inspections--measurement-slider-disabled-hint-value="Enter a value…">
 *     <span data-inspections--measurement-slider-target="hint"></span>
 *     <span data-inspections--measurement-slider-target="bubble">4 mm</span>
 *     <input type="range" step="1" data-step="1"
 *            data-inspections--measurement-slider-target="input"
 *            data-action="input->inspections--measurement-slider#onInput
 *                         pointerdown->inspections--measurement-slider#restoreStep
 *                         keydown->inspections--measurement-slider#restoreStep" />
 *     <input type="number" step="any"
 *            data-inspections--measurement-slider-target="number"
 *            data-action="input->inspections--measurement-slider#onNumberInput
 *                         change->inspections--measurement-slider#onNumberChange" />
 *   </div>
 */
// Float tolerance when comparing a reading with the slider's step grid.
const STEP_EPSILON = 1e-9

export default class extends Controller {
  static targets = ["input", "bubble", "hint", "number"]
  static values = { disabledHint: String }

  connect() {
    // Stash the localised hint text on the hint node so the severity engine
    // (which toggles the disabled state) can show/hide it without re-reading
    // the controller value.
    if (this.hasHintTarget && this.hasDisabledHintValue) {
      this.hintTarget.dataset.disabledHintText = this.disabledHintValue
    }
    this.restoreStoredReading()
    this.updateBubble()
    this.updateFill()

    // The severity engine disables the RANGE when an enabled-if gate is unmet;
    // the box follows it, whoever flips it.
    if (this.hasInputTarget && this.hasNumberTarget) {
      this.syncDisabled()
      this.disabledObserver = new MutationObserver(() => this.syncDisabled())
      this.disabledObserver.observe(this.inputTarget, {
        attributes: true,
        attributeFilter: ["disabled"],
      })
    }
  }

  disconnect() {
    this.disabledObserver?.disconnect()
    this.disabledObserver = null
  }

  onInput() {
    this.updateBubble()
    this.updateFill()
    // Our own dispatch from the box: writing the range's value back would turn
    // a half-typed "3." into "3" under the inspector's fingers.
    if (this.echoing) return
    this.writeNumber(this.inputTarget.value)
  }

  onNumberInput() {
    if (!this.hasInputTarget || !this.hasNumberTarget) return
    const typed = this.parse(this.numberTarget.value)
    if (typed === null) return
    this.moveRangeTo(this.clamp(typed))
  }

  // Committed (blur / Enter): an empty or out-of-range entry shows what the
  // range — the saved field — actually holds.
  onNumberChange() {
    if (!this.hasInputTarget || !this.hasNumberTarget) return
    const typed = this.parse(this.numberTarget.value)
    if (typed === null || typed !== this.clamp(typed)) {
      this.writeNumber(this.inputTarget.value)
    }
  }

  // The rendered reading, if the browser snapped it to the step on parse.
  // Only then: an on-step reading was never snapped, and a range that no
  // longer holds the snapped value was moved by the inspector before this
  // controller connected (a slow page), which must not be undone.
  restoreStoredReading() {
    if (!this.hasInputTarget) return
    const range = this.inputTarget
    const stored = this.parse(range.getAttribute("value"))
    const step = this.parse(range.dataset.step)
    if (stored === null || stored !== this.clamp(stored)) return
    if (step === null || step <= 0) return

    const min = this.parse(range.min) ?? 0
    const steps = (stored - min) / step
    if (Math.abs(steps - Math.round(steps)) < STEP_EPSILON) return

    const snapped = min + Math.round(steps) * step
    const current = this.parse(range.value)
    if (current === null || Math.abs(current - snapped) > STEP_EPSILON) return

    this.moveRangeTo(stored)
  }

  restoreStep(event) {
    if (!this.hasInputTarget) return
    if (event?.type === "keydown" && !RANGE_KEYS.has(event.key)) return
    const range = this.inputTarget
    const step = range.dataset.step
    if (!step || range.getAttribute("step") === step) return

    const before = range.value
    range.step = step
    // A browser may snap the value to the restored step; tell everyone.
    if (range.value !== before) this.dispatchRangeInput()
  }

  moveRangeTo(value) {
    const range = this.inputTarget
    range.step = "any"
    range.value = String(value)
    this.echoing = true
    try {
      this.dispatchRangeInput()
    } finally {
      this.echoing = false
    }
  }

  dispatchRangeInput() {
    this.inputTarget.dispatchEvent(new Event("input", { bubbles: true }))
  }

  writeNumber(raw) {
    if (!this.hasNumberTarget) return
    const num = this.parse(raw)
    this.numberTarget.value = num === null ? "" : String(num)
  }

  syncDisabled() {
    this.numberTarget.disabled = this.inputTarget.disabled
  }

  // The touch track's filled share (`--measurement-fill`, conek_ui
  // measurement_slider.css).
  updateFill() {
    if (!this.hasInputTarget) return
    const range = this.inputTarget
    const min = this.parse(range.min) ?? 0
    const max = this.parse(range.max) ?? 100
    const value = this.parse(range.value) ?? min
    const share =
      max > min
        ? Math.min(Math.max(((value - min) * 100) / (max - min), 0), 100)
        : 0
    range.style.setProperty("--measurement-fill", `${share}%`)
  }

  clamp(value) {
    const min = this.parse(this.inputTarget.min)
    const max = this.parse(this.inputTarget.max)
    let clamped = value
    if (min !== null && clamped < min) clamped = min
    if (max !== null && clamped > max) clamped = max
    return clamped
  }

  parse(raw) {
    if (raw === null || raw === undefined || String(raw).trim() === "")
      return null
    const num = Number(raw)
    return Number.isFinite(num) ? num : null
  }

  updateBubble() {
    if (!this.hasInputTarget || !this.hasBubbleTarget) return
    const raw = this.inputTarget.value
    const num = parseFloat(raw)
    const display = Number.isInteger(num) ? String(num) : String(num)
    const unit = this.unitFromBounds()
    this.bubbleTarget.textContent = unit ? `${display} ${unit}` : display
  }

  // The unit is rendered into the min/max captions; derive it for the bubble by
  // reading the trailing token off the input's aria, falling back to none.
  unitFromBounds() {
    return this.bubbleTarget.dataset.unit || this.currentUnit()
  }

  currentUnit() {
    // The bubble's initial server-rendered text already includes the unit;
    // keep it stable by parsing it once.
    if (this._unit !== undefined) return this._unit
    const initial = this.bubbleTarget.textContent.trim().split(/\s+/)
    this._unit = initial.length > 1 ? initial[initial.length - 1] : ""
    return this._unit
  }
}
